import { AuditPackage } from '~/models/audit/package'
import { AuditShop } from '~/models/audit/shop'
import { computed } from '~/utils/nuxt3-migration'
import { ciMakeBikecheck } from '~/icons/source/regular/make-bikecheck'
import { ciMakeVancheck } from '~/icons/source/regular/make-vancheck'
import { ciMakeCarcheck } from '~/icons/source/regular/make-carcheck'
import { useNamespacedStore } from '~/compositions/store'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'

export function useAudits() {
  const { getters } = useNamespacedStore(CLASSIFIED_VIEW_NS)
  const forBikes = computed<boolean>(() => getters('isBikeClassified'))
  const forVans = computed<boolean>(() => getters('isVanClassified'))

  function getUniqueOptions(packages: AuditPackage[] = []) {
    const map = new Map()
    packages
      .map(p => p.auditOptions)
      .flat()
      .forEach(p => map.set(p.name, p))
    return Array.from(map.values())
  }

  function getNormalizedPerks(
    perksPerPackage: {
      id: number
      index: number
      perks: string[]
    }[],
    auditPackage: AuditPackage
  ) {
    if (!perksPerPackage.some(p => p.perks.length > 0)) {
      return []
    }
    const numberOfPackages = perksPerPackage.length
    const lastPackagePerks = perksPerPackage[numberOfPackages - 1]
    const currentPerks = perksPerPackage.find(p => p.id === auditPackage.id)

    const packageWithTheMostPerks = perksPerPackage.reduce((max, current) => {
      return current.perks.length > max.perks.length ? current : max
    }, perksPerPackage[0])

    if (currentPerks?.index === 0 && !currentPerks.perks.length) {
      return lastPackagePerks.perks.map(p => ({ text: p, active: false }))
    } else if (
      currentPerks?.perks &&
      currentPerks.perks.length < packageWithTheMostPerks.perks.length
    ) {
      const uniquePerks = packageWithTheMostPerks.perks.filter(
        p => !currentPerks.perks.includes(p)
      )
      const repeats = currentPerks.perks.length - uniquePerks.length

      return [
        formatPerks(currentPerks.perks),
        formatPerks(uniquePerks.slice(0, repeats), false)
      ].flat()
    }
    return formatPerks(currentPerks?.perks)
  }

  function formatPerks(perks: string[] = [], isActive = true) {
    return perks.map(p => ({ text: p, active: isActive })) || []
  }

  function getPerksPerPackage(selectedShopOption: AuditShop) {
    return selectedShopOption.packages.map((p, index: number) => ({
      id: p.id,
      perks: p.perks,
      index
    }))
  }

  const placeholderIcon = computed(() => {
    if (forBikes.value) {
      return ciMakeBikecheck
    } else if (forVans.value) {
      return ciMakeVancheck
    }
    return ciMakeCarcheck
  })

  return {
    getUniqueOptions,
    getNormalizedPerks,
    getPerksPerPackage,
    formatPerks,
    placeholderIcon
  }
}
